/* eslint-disable lingui/no-unlocalized-strings -- valores técnicos dos enums do fio, não texto de UI */
import { z } from 'zod';

// Fio da Régua de cobrança (BillingApi `/BillingSetting`), no formato do `BillingSettingDto` do
// backend (discovery/modulos/billing-setting §6). Enums chegam como texto (JsonStringEnumConverter).

const text = z.string().nullish();
const count = z.number().int().nullish();

/** Tipo de juros e de multa (`RateTypeEnum`). */
export const FEE_TYPES = ['Free', 'Percent', 'Money'] as const;
export type FeeType = (typeof FEE_TYPES)[number];

/** Tipo da taxa de liquidação antecipada (`AdvanceFeeTypeEnum`). */
export const ADVANCE_FEE_TYPES = ['Percent', 'Amount'] as const;
export type AdvanceFeeType = (typeof ADVANCE_FEE_TYPES)[number];

/**
 * Valor de juros ou multa: **reais** no tipo `Money` (`Juros.ToDecimalUY3`, 4 casas) e **fração** no
 * `Percent` (0.02 = 2%). O número é o do fio; a tela converte com `big.js` (billing-setting.mappers).
 */
const feeAmount = z.number();

export const billingSettingSchema = z.looseObject({
  id: z.string(),
  name: text,
  walletCode: z.number(),
  walletNumber: count,
  interestTypeValue: text,
  interestTypeValueDisplay: text,
  interestAmount: feeAmount.nullish(),
  interestCodeValue: text,
  interestCodeValueDisplay: text,
  fineTypeValue: text,
  fineTypeValueDisplay: text,
  fineAmount: feeAmount.nullish(),
  daysToAutoBankSlipCancel: count,
  daysToAutoBankSlipRegister: count,
  bankSlipMessage: text,
  firstInstructionDebtCollection: text,
  secondInstructionDebtCollection: count,
  includeAdvanceFee: z.boolean().nullish(),
  /** `""` quando não há taxa (`Nullable<enum>.ToString()` no backend). */
  advanceFeeTypeValue: text,
  advanceFeeTypeValueDisplay: text,
  /** Centavos no tipo `Amount` (o legado não converte) e fração no `Percent`. */
  advanceFeeValue: feeAmount.nullish(),
});
export type BillingSetting = z.infer<typeof billingSettingSchema>;

/** Envelope paginado da BillingApi (`AbstractPaginatedDTO`). */
export const billingSettingPageSchema = z.looseObject({
  data: z.array(billingSettingSchema),
  page: z.number(),
  totalItems: z.number(),
  totalPages: z.number(),
  isCountTruncated: z.boolean().nullish(),
});
export type BillingSettingPage = z.infer<typeof billingSettingPageSchema>;

/** Opção de régua para o autocomplete de outros módulos (Cessionário, Nova cobrança): `(código) - nome`. */
export const billingSettingOptionSchema = z.looseObject({ id: z.string(), walletCode: z.union([z.number(), z.string()]).nullish(), name: text });
export const billingSettingOptionPageSchema = z.looseObject({ data: z.array(billingSettingOptionSchema), page: z.number(), totalItems: z.number(), totalPages: z.number() });
export type BillingSettingOption = z.infer<typeof billingSettingOptionSchema>;

/** Query da lista: filtros na ordem aplicada, depois `page`, `size` e `tenant` (billingParametersContext.tsx:39). */
export interface BillingSettingListParams {
  readonly filters: Readonly<Record<string, unknown>>;
  readonly page: number;
  readonly size: number;
  readonly tenant: string | undefined;
}

export function billingSettingListQuery(params: BillingSettingListParams): Record<string, unknown> {
  return { ...params.filters, page: params.page, size: params.size, tenant: params.tenant };
}

/**
 * Corpo de `POST`/`PUT /BillingSetting`, como o legado monta (Drawer…Container.tsx:60-68): os valores
 * do formulário depois do cast do yup, e na edição também as chaves da linha que não são campo.
 */
export interface BillingSettingBody {
  readonly [key: string]: unknown;
  /** Novo: texto (cast do yup `string()`); edição: o número da linha. */
  readonly walletCode: string | number;
  readonly name: string;
  readonly interestTypeValue: FeeType;
  readonly interestAmount: number | null;
  readonly interestCodeValue: string | null;
  readonly fineTypeValue: FeeType;
  readonly fineAmount: number | null;
  /** Texto, como o cast do yup `string()` entrega. */
  readonly daysToAutoBankSlipCancel: string | null;
  readonly daysToAutoBankSlipRegister: string | null;
  readonly bankSlipMessage: string | null;
  readonly firstInstructionDebtCollection: string | null;
  readonly secondInstructionDebtCollection: number | null;
  readonly includeAdvanceFee: boolean;
  readonly advanceFeeTypeValue: AdvanceFeeType | null;
  readonly advanceFeeValue: number | null;
}
